// Phase 3.1 — ALM Alerts API hooks

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import { AlertListResponse, AlmAlert } from './types';

// ── Query keys ────────────────────────────────────────────────────────────────

export const alertKeys = {
  all:     (ack: boolean) => ['alm', 'alerts', { acknowledged: ack }] as const,
  count:   ()             => ['alm', 'alerts', 'count'] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * List ALM alerts.
 * @param acknowledged  false = unacknowledged only (default); true = full history
 */
export function useAlerts(acknowledged = false) {
  return useQuery({
    queryKey: alertKeys.all(acknowledged),
    queryFn: async () => {
      const url = `${apiPath('alm', 'alerts')}?acknowledged=${acknowledged}`;
      const data = await apiGet<AlertListResponse>(url);
      return data;
    },
    // Refresh every 60 s so new breach alerts surface quickly
    refetchInterval: 60_000,
  });
}

/**
 * Acknowledge a single alert.
 * Invalidates both the unacknowledged and full-history query caches.
 */
export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation<AlmAlert, CubixApiError, string>({
    mutationFn: (alertId: string) =>
      apiPost<AlmAlert, Record<string, never>>(
        `${apiPath('alm', 'alerts')}/${alertId}/acknowledge`,
        {}
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alm', 'alerts'] });
    },
  });
}

/**
 * Manually trigger the nightly batch (ALM_ADMIN only).
 */
export function useTriggerBatch() {
  const qc = useQueryClient();
  return useMutation<unknown, CubixApiError, string | undefined>({
    mutationFn: (date?: string) => {
      const url = date
        ? `${apiPath('alm', 'batch', 'run')}?date=${date}`
        : apiPath('alm', 'batch', 'run');
      return apiPost(url, {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alm', 'alerts'] });
    },
  });
}
